<template>
  <div class="w-full h-lvh absolute top-0 left-0 overflow-hidden" data-testid="txn-map">
    <OpenLayerMap
      ref="map"
      :center="mapview.center"
      :extent="mapview.extent"
      :zoom="mapview.zoom"
      :minZoom="mapview.minZoom"
      :maxZoom="mapview.maxZoom"
      :projection="mapview.projection"
      :baseLayers="mapview.baseLayers"
      :infoLayers="mapview.infoLayers"
      :overlay="mapview.overlay"
      :baseLayer="mapview.baseLayer"
      :connectFeaturesCollection="true"
      :connectFeaturesMinZoom="mapview.connectFeaturesMinZoom"
      :enableSelectFeature="true"
      :brokerOverlayEnabled="brokerOverlayEnabled"
      :recentlyViewedEnabled="recentlyViewedEnabled"
      @viewport-change="onViewportChange"
      @ready="onMapReady"
      @features-selected="handleFeaturesSelected"
    />

    <!-- Cluster Popover Anchor -->
    <div
      v-if="showClusterPopover"
      ref="popoverAnchor"
      class="absolute w-1 h-1 pointer-events-none"
      :style="{ left: popoverPosition.x + 'px', top: popoverPosition.y + 'px' }"
    />
    <Popover ref="clusterPopover" append-to="body" @hide="onPopoverHide" :dismissable="false">
      <div class="flex flex-col w-72">
        <span class="font-medium text-sm text-surface-600 dark:text-surface-400 mb-1">
          {{ $t('messages.transactions.select_transaction') }}
          <span class="text-surface-400 tabular-nums">({{ clusterIds.length }})</span>
        </span>
        <div
          class="flex flex-col max-h-64 overflow-auto gap-1 cluster-popover-scroll"
          @scroll.passive="onClusterScroll"
        >
          <div
            v-for="transaction in clusterTransactions"
            :key="transaction.id"
            @click="selectTransactionFromCluster(transaction)"
            class="flex items-center text-sm justify-between px-3 py-2 cursor-pointer hover:bg-primary-100/75 bg-gray-200/70 dark:hover:bg-primary-900 text-gray-700 dark:text-gray-300 rounded-md transition-colors"
            :class="{ 'opacity-60': isTransactionViewed(transaction.id) }"
          >
            <span class="text-sm inline-flex items-center gap-1.5">
              {{ transaction.kpv }} {{ transaction.tehing }} {{ transaction.tehingusumma }}
              <i
                v-if="isTransactionViewed(transaction.id)"
                class="pi pi-eye text-xs text-surface-400"
                :title="$t('messages.transactions.viewed')"
              ></i>
            </span>
            <Badge :severity="getIconVariant(transaction)">&nbsp;</Badge>
          </div>
          <div
            v-if="clusterLoadingMore"
            class="flex items-center justify-center py-2 text-surface-400"
          >
            <i class="pi pi-spin pi-spinner text-sm"></i>
          </div>
        </div>
      </div>
    </Popover>


    <!-- Add loading indicator -->
    <div v-if="mapStore.loading" class="absolute bottom-4 right-4 p-3 bg-white rounded-sm shadow-sm z-10">
      <i class="pi pi-spin pi-spinner text-lg"></i>
      <span class="ml-2">{{ $t('messages.loading') }}</span>
    </div>
  </div>
</template>

<script setup>
import { cloneDeep, debounce } from 'lodash-es'
import { storeToRefs } from 'pinia'
import { nextTick, onMounted, reactive, ref, shallowRef, watch } from 'vue'
import { useDetailMapHelpers } from '@/composables/useDetailMapHelpers'
import { useHelpers } from '@/composables/useHelpers'
import { useMapStore } from '@/stores/map'
import { useRecentlyViewedStore } from '@/stores/recentlyViewed'
import { useTransactionStore } from '@/stores/transaction'
import {
  DATA_PROJECTION,
  DETAIL_MACRO_MAP_FEATURE_LAYER,
  LAYERS,
  MAP_CENTER,
  MAP_DEFAULT_BASELAYER,
  MAP_EXTENT,
  MAP_MAXZOOM,
  MAP_MINZOOM,
  MAP_PROJECTION,
  MAP_ZOOM,
  TRANSACTION_FEATURE_ICONS,
  TRANSACTION_MAP_BASELAYERS,
  TRANSACTION_MAP_CONNECT_FEATURES_ZOOM,
  TRANSACTION_MAP_FEATURE_LAYER,
  TRANSACTION_MAP_INFO_LAYERS,
} from '../../constants'
import { createGeoJsonFeature, decode } from '../../services/map/FeatureService'
import { transform } from '../../services/map/ProjectionService'
import { createTransactionsStyle } from '../../services/map/StyleService'
import { getB64Svg } from '../../services/utilities'
// import MapComponent from '../map/MapComponent.vue'
import OpenLayerMap from '../map/OpenLayerMap.vue'

const { findLayer } = useDetailMapHelpers()
const { iconVariant } = useHelpers()

const transactionStore = useTransactionStore()
const {
  searchMetadata,
  transactionMapRequestParams,
  markedTransactions,
} = storeToRefs(transactionStore)

// RED-236: cluster-popover row "viewed" dimming reads the recently-viewed store
// (the single source of truth), so it stays consistent with the marker badge and
// survives reloads. Supersedes RED-255's earlier session-only viewed-set.
const recentlyViewedStore = useRecentlyViewedStore()
const { viewedIds } = storeToRefs(recentlyViewedStore)
const isTransactionViewed = id => viewedIds.value.has(id)

const mapStore = useMapStore()
const { label: activeLabel } = storeToRefs(mapStore)

const emit = defineEmits(['update:open', 'features-selected'])

// Define props
const props = defineProps({
  geom: Object,
  // RED-180 — only the standalone /transactions page (no objectId) should
  // render the broker-selection orange "+" rings. Other mount points (e.g.
  // TransactionsDialog on the evaluation page) leave this false so a
  // lingering brokerSelectionStore from an earlier session doesn't leak
  // rings into the Hindamine flow.
  brokerOverlayEnabled: {
    type: Boolean,
    default: false,
  },
  // RED-236 — the "viewed" badge, open-marker halo, and recently-viewed
  // recording are standalone-Tehingute-kaart only. Left false on other mount
  // points (e.g. TransactionsDialog on the evaluation page) so the shared map
  // component doesn't leak the visuals or pollute the per-user recently-viewed
  // list — mirrors brokerOverlayEnabled (RED-180).
  recentlyViewedEnabled: {
    type: Boolean,
    default: false,
  },
})

// Component state
const icons = ref(TRANSACTION_FEATURE_ICONS)
const viewportSearchEnabled = ref(false)
const clusteringEnabled = ref(true)
const clusterDistance = ref(40)

const map = ref(null)

// Cluster popover state. Rows load in pages of CLUSTER_PAGE_SIZE and the
// scroll box fetches the next page near its bottom — a 99+ cluster must not
// fire one request for every member at once.
const CLUSTER_PAGE_SIZE = 50
const clusterPopover = ref(null)
const popoverAnchor = ref(null)
const popoverPosition = ref({ x: 0, y: 0 })
const showClusterPopover = ref(false)
const clusterTransactions = shallowRef([])
const clusterIds = shallowRef([])
const clusterLoadingMore = ref(false)
// Bumped on every new cluster click/close so in-flight pages of a previous
// cluster can't append into the new one
let clusterLoadSequence = 0

// Core mapview configuration
const mapview = reactive({
  center: transform('fromLonLat', MAP_CENTER, MAP_PROJECTION),
  extent: transform('extent', MAP_EXTENT, 'EPSG:4326', MAP_PROJECTION),
  zoom: MAP_ZOOM,
  minZoom: MAP_MINZOOM,
  maxZoom: MAP_MAXZOOM,
  projection: MAP_PROJECTION,
  baseLayers: TRANSACTION_MAP_BASELAYERS.map(id => findLayer(id, false)),
  infoLayers: [],
  overlay: null,
  baseLayer: MAP_DEFAULT_BASELAYER,
  connectFeaturesMinZoom: TRANSACTION_MAP_CONNECT_FEATURES_ZOOM,
})

// Methods
const featureLayer = id => {
  const layer = findLayer(id, true)
  if (layer && icons.value) {
    // Create configuration for different marker types
    const conf = {
      default: {
        color: '#027BFA', // Blue
      },
      ad_link: {
        color: '#2DA74E', // Green
      },
      not_vabaturu_tehing: {
        color: '#FF4081', // Pink
      },
      marked_for_comparison: {
        color: '#DB3848', // Red
      },
      cluster: {
        color: '#027BFA', // Blue
      },
      cluster_ad_link: {
        color: '#2DA74E', // Green
      },
      cluster_marked_for_comparison: {
        color: '#DB3848', // Red
      },
      // Configuration for "open" state ring highlight
      openRing: {
        color: '#6366f1', // Indigo - matches search highlight
        width: 2.2,
        scale: 1.1,
      },
    }

    // Configure layer for WebGL rendering with optimized clustering
    layer.webgl = true

    // Set clustering configuration for better performance
    layer.clusterDistance = clusteringEnabled.value ? clusterDistance.value : 0
    layer.clusterMinDistance = 20

    // Use WebGL style instead of regular style for better performance with large datasets
    layer.style = createTransactionsStyle(conf)

    // Add optimization hints for WebGL rendering
    layer.declutter = false // Disable decluttering for better performance
    layer.updateWhileAnimating = true // Update during animations
    layer.updateWhileInteracting = true // Update during interactions
  }
  return layer
}

// Handle map viewport changes
const onViewportChange = viewport => {
  transactionMapRequestParams.value.zoom = viewport.zoom
  transactionMapRequestParams.value.bbox = viewport.extent
  mapStore.viewport.value = viewport

  // Always call addFeaturesToMap — the loader internally skips API requests
  // when zoom/filters haven't changed (pure pan). At high zoom (13+), it
  // only re-fetches if the viewport has panned beyond the pre-loaded buffer.
  // immediate: moveend already debounced this; a second debounce is pure lag.
  nextTick(() => {
    if (map.value) {
      map.value.addFeaturesToMap({ immediate: true })
    }
  })
}

// Lifecycle hooks
onMounted(() => {
  // Initialize map layers after the component is mounted
  nextTick(() => {
    mapview.overlay = featureLayer(TRANSACTION_MAP_FEATURE_LAYER)
    mapview.overlay.filter = ['any']
    const infoLayers = TRANSACTION_MAP_INFO_LAYERS.map(id =>
      findLayer(id, false),
    )

    mapview.infoLayers = infoLayers
  })
})

const onMapReady = () => {
  if (props.geom && props.geom !== 'undefined') {
    map.value.addGeometryFeature(props.geom[0])
  }
}


// Cluster popover functions
const handleFeaturesSelected = async event => {
  const { ids, pixel, isCluster, feature } = event

  if (!ids || !ids.length) {
    showClusterPopover.value = false
    clusterPopover.value?.hide()
    return
  }

  // Single transaction - emit directly to open sidebar
  if (!isCluster || ids.length === 1) {
    showClusterPopover.value = false
    emit('features-selected', event)
    return
  }

  // Multiple transactions (cluster) - show popover with the first page
  const sequence = ++clusterLoadSequence
  try {
    clusterPopover.value?.hide()
    const transactions = await transactionStore.loadClusterTransactions(
      ids,
      0,
      CLUSTER_PAGE_SIZE,
    )
    if (sequence !== clusterLoadSequence) {
      return
    }
    clusterIds.value = ids
    clusterTransactions.value = transactions
    popoverPosition.value = { x: pixel[0] - 21, y: pixel[1] + 10 }
    showClusterPopover.value = true

    await nextTick()
    if (popoverAnchor.value && clusterPopover.value) {
      clusterPopover.value.show({ currentTarget: popoverAnchor.value })
    }
  } catch (error) {
    console.error('Error loading cluster transactions:', error)
    // Fallback to direct emit if loading fails
    emit('features-selected', event)
  }
}

const loadMoreClusterTransactions = async () => {
  if (
    clusterLoadingMore.value ||
    clusterTransactions.value.length >= clusterIds.value.length
  ) {
    return
  }
  clusterLoadingMore.value = true
  const sequence = clusterLoadSequence
  try {
    const page = await transactionStore.loadClusterTransactions(
      clusterIds.value,
      clusterTransactions.value.length,
      CLUSTER_PAGE_SIZE,
    )
    if (sequence !== clusterLoadSequence) {
      return
    }
    clusterTransactions.value = [...clusterTransactions.value, ...page]
  } catch (error) {
    console.error('Error loading more cluster transactions:', error)
  } finally {
    clusterLoadingMore.value = false
  }
}

const onClusterScroll = event => {
  const el = event.target
  if (el.scrollTop + el.clientHeight >= el.scrollHeight - 48) {
    loadMoreClusterTransactions()
  }
}

// Handle popover hide event. Deliberately no state reset here: hide() can
// fire mid-way through a newer cluster's open (transition timing) and a
// reset would wipe the fresh list. Every open replaces the state anyway,
// and the sequence guard drops any stale in-flight page.
const onPopoverHide = () => {
  showClusterPopover.value = false
}

const selectTransactionFromCluster = transaction => {
  // showClusterPopover.value = false
  // clusterPopover.value?.hide()
  transactionStore.handleListFeatureSelection([transaction.id])
}

const getIconVariant = transaction => {
  return iconVariant(transaction, markedTransactions.value)
}

defineExpose({
  map,
})
</script>

<style scoped>
  @reference '../../../css/tailwind.css';

  .map-wrapper {
    position: relative;
  }

  /* Cluster popover custom scrollbar */
  .cluster-popover-scroll {
    scrollbar-width: thin;
    scrollbar-color: rgb(156 163 175) transparent;

    &::-webkit-scrollbar {
      width: 8px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
      border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
      @apply bg-gray-300 dark:bg-gray-600;
      border-radius: 4px;
      border: 2px solid transparent;
      background-clip: content-box;

      &:hover {
        @apply bg-gray-400 dark:bg-gray-500;
      }
    }
  }
</style>
